import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import type { TFunction } from "i18next";
import {
  ChartLineIcon,
  CornerLeftUpIcon,
  FileTextIcon,
  UsersIcon,
} from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { Divider } from "simplydo/react/ui/divider";
import { Icon } from "simplydo/react/ui/icon";

import type {
  IdeaImpactDisplayMeasure,
  ImpactStatementImpactValue,
  OrganisationImpact,
} from "../types";
import { getImpactTypeIcon, isTimeImpactType } from "./impactIcons";

type Impact =
  OpenAPI.GET<"/organisations/{id}/impacts">["response"]["impacts"][number];

export const impactStageDefinitions: Record<
  NonNullable<OpenAPI.Schemas["ImpactStatement"]["stage"]>,
  {
    name: string;
    previousStage?: NonNullable<OpenAPI.Schemas["ImpactStatement"]["stage"]>;
    rangeInputType?: "auto" | "allowCustom";
  }
> = {
  // The user stage is the first stage where users can estimate the impact of their idea
  // The input type is exactly as configured in the challenge
  userEstimate: { name: "User Estimate", rangeInputType: "auto" },
  // In the project estimation phase the project team can provide their own estimate
  projectEstimate: {
    name: "Project Estimate",
    previousStage: "userEstimate",
    rangeInputType: "auto",
  },
  // For the final outcome the input type is allowed to have a custom input value
  finalOutcome: {
    name: "Final Outcome",
    previousStage: "projectEstimate",
    rangeInputType: "allowCustom",
  },
};
export const impactStageOrder = [
  "userEstimate",
  "projectEstimate",
  "finalOutcome",
] as const;

export const impactMultipliers = {
  "one-off": 1,
  annually: 1,
  monthly: 12,
  weekly: 52,
};
type Frequency = keyof typeof impactMultipliers;

const forceNumber = (value: string, multiplier?: number) => {
  if (!value) return;
  const number = Number(value);
  if (Number.isNaN(number)) return;
  return number * (multiplier ?? 1);
};

const formatNumber = (value: string, multiplier?: number) => {
  const number = forceNumber(value, multiplier);
  if (!number) return value;

  if (window.Intl) {
    return new Intl.NumberFormat().format(number);
  }
  return `${number}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const formatRange = (
  impact: Impact,
  from: string,
  to?: string,
  options?: { frequency?: Frequency },
) => {
  if (!impact || from === undefined || from === null || from === "") {
    return "";
  }

  let fromValue: string | number = from;
  let toValue: string | number | undefined = to;

  let prefix = "";
  let suffix = "";

  const multiplier = options?.frequency
    ? (impactMultipliers[options.frequency] ?? 1)
    : 1;

  if (isTimeImpactType(impact.type)) {
    fromValue = forceNumber(fromValue, multiplier) ?? fromValue;
    if (toValue) {
      toValue = forceNumber(toValue, multiplier) ?? toValue;
    }
    suffix = " hours";
  } else {
    fromValue = formatNumber(fromValue, multiplier);
    if (toValue) {
      toValue = formatNumber(toValue, multiplier);
    }
  }

  if (impact.type === "money") {
    prefix = "£";
  }
  return `${prefix}${fromValue}${toValue ? ` - ${toValue}` : ""}${suffix}`;
};

export const formatImpact = (
  orgImpact: Impact | undefined,
  impact: IdeaImpactDisplayMeasure,
  options?: { annually?: boolean },
) => {
  if (!orgImpact) return "";
  return formatRange(
    orgImpact,
    String(impact.choice?.value ?? impact.value ?? ""),
    impact.choice?.toRange,
    {
      frequency: options?.annually
        ? (impact.frequency ?? "annually")
        : undefined,
    },
  );
};

export const formatOption = (
  impact: Impact,
  option: { value: string; toRange?: string },
) => {
  return formatRange(impact, option.value, option.toRange);
};

export const annualImpactValue = (impact: IdeaImpactDisplayMeasure) => {
  let value = "";
  const ownerImpact = impact.ownerImpact;
  if (ownerImpact) {
    value = formatImpact(ownerImpact, impact, { annually: true });
  }

  return value;
};

export const generateImpactText = (
  impact: IdeaImpactDisplayMeasure,
  t: TFunction,
) => {
  const effectMap: Record<string, string> = { cost: "cost", saving: "save" };

  const effectText =
    effectMap[impact?.ownerImpact?.effect ?? ""] ??
    `have ${t("generic.impactWithArticle")} of`;
  let text = `This ${t("generic.idea")} will ${effectText} `;
  if (impact.ownerImpact) {
    text += formatImpact(impact.ownerImpact, impact);
  }

  if (!impact?.frequency || impact?.frequency === "one-off") {
    text += " once";
  } else {
    text += ` ${impact?.frequency}`;
    if (impact?.frequency !== "annually") {
      text += ` (${annualImpactValue(impact)} annually)`;
    }
  }
  text += ".";

  return (
    <>
      <p>
        <strong>{impact?.ownerImpact?.name}</strong>
        {impact?.ownerImpactCategory ? (
          <span>
            <br />
            in {impact?.ownerImpactCategory?.name}
          </span>
        ) : null}
      </p>
      <p>{text}</p>
      {impact.choice?.weight !== undefined ? (
        <div
          style={{ display: "flex", alignItems: "center", lineHeight: "1em" }}
        >
          <Icon
            icon={CornerLeftUpIcon}
            size="small"
            style={{ marginRight: 2 }}
          />
          <div>
            Weight: {impact.choice?.weight || 0}/
            {impact.ownerImpact?.maxWeight || 0}
          </div>
        </div>
      ) : null}
      {impact?.justification ? (
        <>
          <Divider />
          <b>Justification</b>
          <p>{impact?.justification}</p>
        </>
      ) : null}
    </>
  );
};

export { getImpactTypeIcon as getImpactIconName } from "./impactIcons";
export { getImpactTypeIcon as getImpactIcon } from "./impactIcons";

export const useImpactStageMap = () => {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      userEstimate: {
        name: "User Estimate",
        icon: FileTextIcon,
        description: `This estimate is based on the estimation of the user who created the ${t("generic.idea")}.`,
        create: undefined,
      },
      projectEstimate: {
        name: "Project Estimate",
        icon: UsersIcon,
        description: ` This estimate is based on the estimation as part of the ${t("generic.idea")}'s assessment. It is only visible to project assignees and ${t("generic.challenge")} managers.`,
        create: "Create estimate",
      },
      finalOutcome: {
        name: "Final Outcome",
        icon: ChartLineIcon,
        description: `This statement is used to track the realised ${t("generic.impact")} of this ${t("generic.idea")}. It is only visible to project assignees and ${t("generic.challenge")} managers.`,
        create: "Create outcome",
      },
    }),
    [t],
  );
};
